// COP — the WIRED multi-INT "Hormuz" picture (the REAL demonstrated COP).
//
// This is the picture the demonstrator filters per clearance. Unlike the legacy
// synthetic mock (host/scenario.ts — cosmetic labels on ~30 fictional tracks),
// every record here carries REAL corpus markings (classification + REL TO +
// SCI/SAP compartments + dissem caveats) drawn from the multi-INT Strait-of-Hormuz
// dataset. Each record is published as a CanonicalEnvelope onto the SAME bus →
// LOCAL PDP (I4) → render path as every other object, so the clearance filter
// bites on the WHOLE picture: logging in as a different persona re-resolves all
// 121 contacts two-state (permit → show / deny → absent), not just the 4 seeded
// KAS TDFs.
//
// The markings are routed to the policy bundle BY VALUE (deriveBoundFqns) — the
// identical algorithm the KAS-seeder uses — so the local filter is faithful to
// what the KAS would decide (lockstep by construction for the values it shares).
//
// Source of truth: ./hormuz.json (field-for-field the track shape). We only
// PROJECT it into the canonical envelope here; we do NOT mutate any contract or
// schema — confidence + heading/speed ride in the open `properties` bag (P1).

import type {
  CanonicalEnvelope,
  Affiliation,
  EnvelopeKind,
  PayloadMode,
  LeakageProfile,
} from "../../../../packages/schema/ts/envelope.ts";
import type { EnvelopeBus } from "../host/envelopeBus.ts";
import hormuzData from "./hormuz.json";
import {
  type TrustTier,
  trustTierOfRecord,
  isPresenceRecord,
  decayedConfidence,
} from "../host/livingNet.ts";
import { altitudeHaeM } from "./altitude.ts";
import { positionAtMs, type MotionRecord } from "../../../../packages/motion-model/src/motion.ts";

/** One waypoint in a record's motion model. */
interface Waypoint {
  lat: number;
  lon: number;
}

/** The motion block as authored in hormuz.json (optional, varied by record). */
interface MotionDef {
  model?: "waypoints" | "heading";
  speed_kn?: number;
  loop?: "pingpong" | "loop";
  waypoints?: Waypoint[];
  heading_deg?: number;
  start?: Waypoint;
  duration_s?: number;
  /** Cruise altitude (ft) for airborne tracks, authored in the motion block. Surfaced
   *  on the envelope so the inspector op-grid renders a real FL### instead of "—". */
  altitude_ft?: number;
}

/** The raw record shape in hormuz.json. Markings are corpus-real; positions are
 *  flat {lat, lon} (NOT GeoJSON order — projected to [lon, lat] below). */
interface HormuzRecord {
  id: string;
  kind: string;
  domain: string;
  affiliation: Affiliation;
  type: string;
  name: string;
  callsign?: string;
  lat: number;
  lon: number;
  headingDeg: number;
  speedKn: number;
  classification: string;
  releasability: string[];
  mission?: string[];
  caveats?: string[];
  motion?: MotionDef;
  protected?: boolean;
  orbit?: boolean;
}

/** Per-record live state advanced by the motion model on each tick. */
interface LiveTrack {
  rec: HormuzRecord;
  lat: number;
  lon: number;
  headingDeg: number;
  speedKn: number;
  /** A stable per-record confidence in [0,1] (synthetic-but-coherent; drives the
   *  later smear). Carried in the open properties bag — confidence is a first-class
   *  optional envelope property (no schema change). For the CROWD tier this is the
   *  DECAYED confidence (crowd-ceiling + confirmation-decay applied); authoritative
   *  passes through unchanged. */
  confidence: number;
  /** A stable per-record staleness window (ms) so freshness varies across the set.
   *  PRESENCE nodes get a SHORTER window so their smear alpha reads as a fresh,
   *  pulsing reachability heartbeat (Living-Net part 1). */
  staleMs: number;
  /** Living-Net trust tier: authoritative (solid, dominant) vs crowd (falsifiable
   *  inner-pip costume + decayed confidence). Crowd never overwrites authoritative. */
  trustTier: TrustTier;
  /** True for own-side reachable-now units (blue-force presence heartbeat). */
  presence: boolean;
}

const records = hormuzData as unknown as HormuzRecord[];

// --- Render-kind mapping -----------------------------------------------------
// The tracks capability renders track / alert / observation. The corpus also
// carries `asset` records (static facilities / front-companies / sites in the
// network picture); map those to `observation` so they too flow through the PDP
// filter and render as static intel objects. Everything else passes through.
const RENDER_KIND: Record<string, EnvelopeKind> = {
  track: "track",
  alert: "alert",
  observation: "observation",
  asset: "observation",
};
function renderKind(k: string): EnvelopeKind {
  return RENDER_KIND[k] ?? "observation";
}

// --- Protection treatment ----------------------------------------------------
// An object is treated as TDF-PROTECTED (clear-shell shell + 🔒 badge when
// visible) when it is explicitly flagged, is TOP_SECRET, or carries any SCI/SAP
// compartment marking. Everything else rides in the clear.
const SCI_SAP_PREFIX = /^(sci|compartment|sap|accm)\s*:/i;
function isProtected(rec: HormuzRecord): boolean {
  if (rec.protected) return true;
  if (rec.classification === "TOP_SECRET") return true;
  return (rec.mission ?? []).some((m) => SCI_SAP_PREFIX.test(m));
}

// --- Deterministic synthetic-but-coherent fields -----------------------------
// A stable hash over the id so confidence + staleness are deterministic per
// record (re-running yields the same picture; nothing is random per-frame).
function hash01(s: string): number {
  let h = 2166136261;
  for (let i = 0; i < s.length; i++) {
    h ^= s.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  // Map to [0,1).
  return ((h >>> 0) % 100000) / 100000;
}

/** Birth confidence in a believable spread: friendly/own-force high, unknown/
 *  pending lower, with per-record jitter. This is the AUTHORITATIVE-tier belief;
 *  the crowd tier applies its ceiling + confirmation-decay ON TOP of this in
 *  confidenceFor() (Living-Net part 3). */
function birthConfidence(rec: HormuzRecord): number {
  const base =
    rec.affiliation === "friend"
      ? 0.9
      : rec.affiliation === "hostile"
        ? 0.78
        : rec.affiliation === "neutral"
          ? 0.7
          : rec.affiliation === "unknown"
            ? 0.5
            : 0.4; // pending
  const jitter = (hash01(rec.id) - 0.5) * 0.2; // ±0.10
  return Math.max(0.2, Math.min(0.99, +(base + jitter).toFixed(2)));
}

/** Downstream-confirmation fraction in [0,1] for a CROWD fact: deterministic per
 *  record so the picture is stable (re-running yields the same field). ~half the
 *  crowd contacts read as recently-confirmed (hold near birth), the rest decay —
 *  so the trust texture is genuinely varied, not uniform. (A real write path —
 *  0.3b — would derive this from second-reports / sensor cross-check.) */
function confirmedFraction(rec: HormuzRecord): number {
  return +hash01(rec.id + "confirm").toFixed(3);
}

/** The render confidence that drives the smear. Authoritative passes its birth
 *  confidence through unchanged (ground truth). Crowd applies the crowd ceiling +
 *  confirmation-decay (Living-Net part 3 — rides the CONFIDENCE channel ONLY:
 *  lower confidence => a wider/softer cloud, the falsifiable-crowd read). */
function confidenceFor(rec: HormuzRecord, tier: TrustTier): number {
  const birth = birthConfidence(rec);
  return tier === "crowd"
    ? decayedConfidence("crowd", birth, confirmedFraction(rec))
    : birth;
}

/** A varied staleness window so some contacts go stale and the freshness treatment
 *  is real, not uniform. PRESENCE nodes get a SHORTER window (30s..75s) so their
 *  smear alpha reads as a fresh, frequently-refreshed reachability HEARTBEAT — the
 *  Living-Net presence read (part 1). Non-presence contacts keep the wider sensor
 *  window (90s..240s); the FRIENDLY FRAME SHAPE is what disambiguates a presence
 *  heartbeat-dim from an ordinary sensor-staleness-dim (DESIGN-CONTEXT §2.2). */
function staleWindowMs(rec: HormuzRecord, presence: boolean): number {
  if (presence) return 30_000 + Math.round(hash01(rec.id + "beat") * 45_000);
  return 90_000 + Math.round(hash01(rec.id + "stale") * 150_000);
}

// --- Motion ------------------------------------------------------------------
// Motion is now a PURE wall-clock function of (record, absMs) — see ./motion.ts.
// positionAtMs(rec, Date.now()) is anchored to a fixed epoch (MOTION_EPOCH_MS) so
// every client computes the SAME position for the SAME track at the SAME instant
// (deterministic shared picture; no per-session dt accumulation). The waypoint /
// heading / orbit models in motion.ts replicate the legacy step() visual behaviour.

// --- Forward-extrapolated motion path (SHARED CROSS-LANE CONTRACT) -----------
// properties.future_path: up to 8 forward positions [lon, lat, altMeters] at +30s,
// +60s, …, +240s, computed from the deterministic motion model (positionAtMs
// forward-eval). ABSENT for a record with no real motion (zero-speed/static) so the
// render lane (L-RENDER) draws nothing. Additive to properties — NO schema/PDP change.
const FUTURE_PATH_STEPS = 8;
const FUTURE_PATH_STEP_MS = 30_000;

/** The deterministic forward path for a record: positionAtMs at +30s..+240s, each
 *  [lon, lat, altMeters] (the altitude model's HAE metres, 0 when surface). Returns
 *  undefined when the record has no real motion (so future_path is ABSENT — the render
 *  lane handles absence gracefully). Coherent across clients: positionAtMs is
 *  epoch-anchored, so two sessions sampling the same wall-clock instant agree. */
function futurePathFor(
  rec: HormuzRecord,
  altM: number | null,
): Array<[number, number, number]> | undefined {
  const speedKn = rec.motion?.speed_kn ?? rec.speedKn;
  if (!(speedKn > 0)) return undefined; // static contact — no forward trajectory
  const now = Date.now();
  const alt = altM ?? 0;
  const path: Array<[number, number, number]> = [];
  for (let n = 1; n <= FUTURE_PATH_STEPS; n++) {
    const p = positionAtMs(rec as MotionRecord, now + n * FUTURE_PATH_STEP_MS);
    path.push([p.lon, p.lat, alt]);
  }
  return path;
}

// --- Envelope projection -----------------------------------------------------
const iso = (offsetMs = 0): string => new Date(Date.now() + offsetMs).toISOString();

/** Project one live track into a CanonicalEnvelope. The `security` block is the
 *  REAL corpus marking set the LOCAL PDP filters on; `properties` carries the
 *  render hints (affiliation shape, domain glyph, confidence) in the open bag. */
function toEnvelope(t: LiveTrack): CanonicalEnvelope {
  const rec = t.rec;
  const protectedObj = isProtected(rec);
  const leakage: LeakageProfile = protectedObj ? "clear-shell" : "clear";
  const payloadMode: PayloadMode = protectedObj ? "encrypted" : "clear";
  const now = iso();
  // 2.5D-DEPTH: air/UAS tracks get a real HAE altitude (metres) from the pure altitude model.
  const altM = altitudeHaeM(rec.domain, rec.type, rec.id);
  // SHARED CONTRACT: forward motion path for the render lane's smear/trail (absent if static).
  const futurePath = futurePathFor(rec, altM);
  return {
    id: rec.id,
    schema: "scop.envelope/1.0",
    kind: renderKind(rec.kind),
    time: now,
    // Real validity window: started ~one tick ago, stale after a varied window so
    // freshness is genuinely per-contact (not uniform).
    valid_time: { start: iso(-1_000), stale: iso(t.staleMs) },
    // COORDINATE ORDER: hormuz.json is flat {lat, lon}; GeoJSON geometry is
    // [lon, lat]. Emit GeoJSON order (the COP reads coordinates as [lon, lat]).
    // 2.5D-DEPTH: air/UAS carry a 3rd HAE coordinate (metres) so the tilted map lifts them.
    geometry: {
      type: "Point",
      coordinates: altM != null ? [t.lon, t.lat, altM] : [t.lon, t.lat],
    },
    properties: {
      name: rec.name,
      ...(rec.callsign ? { callsign: rec.callsign } : {}),
      affiliation: rec.affiliation,
      type: rec.type,
      domain: rec.domain,
      heading_deg: Math.round(t.headingDeg),
      speed_kn: Math.round(t.speedKn),
      // 2.5D-DEPTH: altitude_m (metres HAE) — canonical Z store the renderer reads for the Z-lift.
      ...(altM != null ? { altitude_m: altM } : {}),
      // Airborne cruise altitude (ft), authored in the motion block → inspector FL###.
      ...(rec.motion?.altitude_ft != null ? { altitude_ft: rec.motion.altitude_ft } : {}),
      // SHARED CONTRACT: future_path — up to 8 forward [lon,lat,altM] positions the render
      // lane reads to draw the predictive smear/trail. Absent when the record is static.
      ...(futurePath ? { future_path: futurePath } : {}),
      // Confidence rides in the open properties bag (first-class optional field).
      // For crowd facts this is the DECAYED confidence — it drives the smear's
      // reach+sigma (Living-Net part 3: trust/confirmation => the confidence channel).
      confidence: t.confidence,
      // ── Living-Net resting-read tags (DESIGN-SPEC §2 CO-HERO, §5 build-step 5) ──
      // trust_tier: authoritative (solid, dominant, unmarked) vs crowd (falsifiable).
      trust_tier: t.trustTier,
      // crowd_pip drives the INNER-PIP costume — a NON-hue/opacity/dash/shape channel
      // (deckBackend.buildCrowdPips). Authoritative carries no pip (the dominant tier).
      crowd_pip: t.trustTier === "crowd",
      // presence: own-side reachable-now units. Their freshness reads as a heartbeat
      // on peak-alpha OPACITY, disambiguated from sensor-staleness by the friendly
      // FRAME SHAPE (Living-Net part 1). presence_seen is the live heartbeat stamp —
      // re-stamped every tick because a reachable node is, by definition, being heard.
      presence: t.presence,
      ...(t.presence ? { presence_seen: now } : {}),
      orig_kind: rec.kind,
      source: "hormuz/multi-int",
    },
    security: {
      classification: rec.classification,
      releasability: rec.releasability,
      mission: rec.mission ?? [],
      caveats: rec.caveats ?? [],
      originator_control: (rec.caveats ?? []).includes("ORCON"),
      policy_ref: "policy://hormuz/multi-int",
      payload_mode: payloadMode,
      leakage_profile: leakage,
    },
    provenance: {
      originator: "hormuz-multi-int",
      adapter: `hormuz/${rec.domain}`,
      received_at: now,
      chain: [{ adapter: `hormuz/${rec.domain}`, at: now }],
    },
    payload: protectedObj
      ? { encrypted_ref: `scop:tdf:${rec.id}` }
      : { clear: { domain: rec.domain } },
  } as CanonicalEnvelope;
}

// --- Public API --------------------------------------------------------------

export interface HormuzHandle {
  stop(): void;
}

/** Build the live track set from the corpus (initial positions + per-record
 *  confidence/staleness). Exposed for tests / inspection. */
export function buildHormuzTracks(): LiveTrack[] {
  return records.map((rec) => {
    // Living-Net classification (deterministic, from the corpus pedigree): trust
    // tier + presence drive the three resting-read channels (DESIGN-SPEC §2 #2).
    const trustTier = trustTierOfRecord(rec);
    const presence = isPresenceRecord(rec);
    return {
      rec,
      lat: rec.lat,
      lon: rec.lon,
      headingDeg: rec.headingDeg,
      speedKn: rec.motion?.speed_kn ?? rec.speedKn,
      confidence: confidenceFor(rec, trustTier),
      staleMs: staleWindowMs(rec, presence),
      trustTier,
      presence,
    };
  });
}

/** Project the corpus to envelopes once (no motion / no live tick) — for a
 *  static publish or a test snapshot. */
export function hormuzEnvelopes(): CanonicalEnvelope[] {
  return buildHormuzTracks().map(toEnvelope);
}

/** Start the live Hormuz picture: emit every contact once, then advance the
 *  motion model + re-emit on each tick so the picture is alive. Every envelope
 *  flows through the bus → PDP filter → render path. */
export function startHormuzScenario(bus: EnvelopeBus, tickMs = 1_000): HormuzHandle {
  const tracks = buildHormuzTracks();
  // Each tick: resolve every track's position PURELY from the wall clock (epoch-anchored),
  // assign it, and re-emit. No dt accumulation, no mutable waypoint cursor — two clients
  // started 60s apart compute the identical picture (deterministic shared COP).
  const emitAll = (): void => {
    const now = Date.now();
    for (const t of tracks) {
      const p = positionAtMs(t.rec as MotionRecord, now);
      t.lat = p.lat;
      t.lon = p.lon;
      t.headingDeg = p.headingDeg;
      void bus.publish(toEnvelope(t));
    }
  };
  emitAll();
  const timer = window.setInterval(emitAll, tickMs);
  return { stop: () => window.clearInterval(timer) };
}
